import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { toast } from 'react-hot-toast';
import { apiClient } from '@/services/apiClient';
import type { User, UserRole, LoginRequest } from '@/types';

interface AuthState {
  // State
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  setLoading: (loading: boolean) => void;
  
  // Helpers
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  isAdmin: () => boolean;
  canAccessAdminSection: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,

      // Actions
      login: async (credentials: LoginRequest): Promise<boolean> => {
        set({ isLoading: true });
        
        try {
          const response = await apiClient.login(credentials);
          
          if (response.error) {
            // Handle specific error cases
            switch (response.status) {
              case 400:
                toast.error('Credenziali non valide. Verifica meccanografico e password.');
                break;
              case 401:
                toast.error('Accesso non autorizzato. Verifica le tue credenziali.');
                break;
              case 429:
                toast.error('Troppi tentativi di login. Riprova tra qualche minuto.');
                break;
              default:
                toast.error(response.error.message || 'Errore durante il login');
            }
            return false;
          }

          if (response.data) {
            const { access, refresh, user } = response.data;
            
            set({
              token: access,
              refreshToken: refresh,
              user,
              isAuthenticated: true,
            });

            // Store in localStorage for persistence
            localStorage.setItem('arma4you.token', access);
            localStorage.setItem('arma4you.refreshToken', refresh);
            localStorage.setItem('arma4you.user', JSON.stringify(user));

            toast.success(`Benvenuto, ${user.nome} ${user.cognome}!`);
            return true;
          }

          return false;
        } catch (error: any) {
          console.error('Login error:', error);
          toast.error('Errore di connessione. Riprova più tardi.');
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      logout: async (): Promise<void> => {
        set({ isLoading: true });
        
        try {
          // Call logout endpoint (optional, for token blacklisting)
          await apiClient.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          // Clear state regardless of API call result
          set({
            token: null,
            refreshToken: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });

          // Clear localStorage
          localStorage.removeItem('arma4you.token');
          localStorage.removeItem('arma4you.refreshToken');
          localStorage.removeItem('arma4you.user');
          localStorage.removeItem('arma4you.season');

          toast.success('Logout effettuato con successo');
        }
      },

      refreshUser: async (): Promise<void> => {
        const { token } = get();
        
        if (!token) {
          return;
        }

        try {
          const response = await apiClient.getCurrentUser();
          
          if (response.data) {
            set({ user: response.data });
            localStorage.setItem('arma4you.user', JSON.stringify(response.data));
          }
        } catch (error) {
          console.error('Failed to refresh user:', error);
          // Don't logout automatically here, let the interceptor handle it
        }
      },

      updateUser: (userUpdate: Partial<User>): void => {
        const { user } = get();
        if (user) {
          const updatedUser = { ...user, ...userUpdate };
          set({ user: updatedUser });
          localStorage.setItem('arma4you.user', JSON.stringify(updatedUser));
        }
      },

      setLoading: (loading: boolean): void => {
        set({ isLoading: loading });
      },

      // Helper methods
      hasRole: (role: UserRole): boolean => {
        const { user } = get();
        if (!user) return false;
        
        // Controllo sicuro per l'array ruoli
        const ruoli = user.ruoli || [];
        const permissions = user.permissions || {};
        
        // Check both ruoli array and permissions object
        return ruoli.includes(role) || permissions[`is_${role}`] === true;
      },

      hasAnyRole: (roles: UserRole[]): boolean => {
        const { hasRole } = get();
        if (!roles || !Array.isArray(roles)) return false;
        return roles.some(role => hasRole(role));
      },

      isAdmin: (): boolean => {
        const { hasAnyRole } = get();
        return hasAnyRole(['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente']);
      },

      canAccessAdminSection: (): boolean => {
        const { isAdmin } = get();
        return isAdmin();
      },
    }),
    {
      name: 'arma4you-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
onRehydrateStorage: () => (state) => {
  // Controllo esplicito che state non sia undefined
  if (!state) return;
  
  // Validate stored auth data on app load
  if (state.token && state.user) {
    // Token exists, user should be authenticated
    state.isAuthenticated = true;
  } else {
    // Clear invalid state
    state.token = null;
    state.refreshToken = null;
    state.user = null;
    state.isAuthenticated = false;
  }
},
    }
  )
);

// Utility hooks for common auth checks


export function forceLogoutAndRedirect() {
  clearTokens();
  const currentPage = window.location.pathname + window.location.search;
  const loginUrl = `/login?redirectTo=${encodeURIComponent(currentPage)}`;
  window.location.replace(loginUrl);  // Redirige alla pagina di login
}

export const useAuth = () => {
  const store = useAuthStore();
  return {
    ...store,
    isLoggedIn: store.isAuthenticated && !!store.user,
  };
};

export const useUser = () => {
  return useAuthStore(state => state.user);
};

export const useIsAuthenticated = () => {
  return useAuthStore(state => state.isAuthenticated && !!state.token);
};

export const useHasRole = (role: UserRole) => {
  return useAuthStore(state => state.hasRole(role));
};

export const useIsAdmin = () => {
  return useAuthStore(state => state.isAdmin());
};

export const useCanAccessAdminSection = () => {
  return useAuthStore(state => state.canAccessAdminSection());
};

// --- TOKENS STORAGE ---
const ACCESS_KEY = 'access_token';
const REFRESH_KEY = 'refresh_token';

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}
export function setTokens(access: string, refresh?: string) {
  localStorage.setItem(ACCESS_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}
export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// --- LOGOUT + REDIRECT ---
export function hardLogout(redirectTo?: string) {
  try { clearTokens(); } catch {}
  // se hai anche userStore, azzera qui (es: useAuth.getState().logout())
  const to = `/login${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`;
  window.location.replace(to); // redirect immediato
}

// --- REFRESH FLOW ---
// Se hai una env/const, usa quella.
// DRF SimpleJWT di solito: '/api/token/refresh/'
const REFRESH_URL = import.meta.env.VITE_REFRESH_URL || '/api/token/refresh/';

export async function refreshAccessToken(api: import('axios').AxiosInstance): Promise<string> {
  const r = getRefreshToken();
  if (!r) throw new Error('No refresh token');

  const res = await api.post(REFRESH_URL, { refresh: r });
  const newAccess: string | undefined = res?.data?.access;
  if (!newAccess) throw new Error('No access in refresh response');

  setTokens(newAccess); // refresh invariato; se il BE ti restituisce anche il refresh, passalo come 2° argomento
  return newAccess;
}
